import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, TrendingUp, Receipt, Coins, 
  ArrowRight, ChevronRight, BarChart3, PiggyBank,
  Target, Wallet, CircleDollarSign, Gem
} from 'lucide-react';

const CryptoCalculatorsHub = () => {
  const calculators = [
    {
      slug: 'crypto-profit-calculator',
      name: 'Crypto Profit Calculator',
      description: 'Calculate profit/loss from any cryptocurrency trade with fees included.',
      icon: TrendingUp,
      color: 'green',
      category: 'general'
    },
    {
      slug: 'crypto-roi-calculator',
      name: 'Crypto ROI Calculator',
      description: 'Calculate return on investment with annualized returns (CAGR) and growth multipliers.',
      icon: BarChart3,
      color: 'blue',
      category: 'general'
    },
    {
      slug: 'crypto-tax-calculator',
      name: 'Crypto Tax Calculator',
      description: 'Estimate capital gains tax on your crypto profits for multiple countries.',
      icon: Receipt,
      color: 'purple',
      category: 'general'
    },
    {
      slug: 'crypto-investment-calculator',
      name: 'Crypto Investment Calculator',
      description: 'Plan your investment strategy with DCA support and target price analysis.',
      icon: PiggyBank,
      color: 'emerald',
      category: 'general'
    },
    {
      slug: 'crypto-gain-calculator',
      name: 'Crypto Gain Calculator',
      description: 'Calculate realized gains with trading fees and cost basis for tax reporting.',
      icon: Target,
      color: 'cyan',
      category: 'general'
    },
    {
      slug: 'bitcoin-profit-calculator',
      name: 'Bitcoin Profit Calculator',
      description: 'Calculate your BTC investment profit with real-time calculations.',
      icon: CircleDollarSign,
      color: 'orange',
      category: 'bitcoin'
    },
    {
      slug: 'bitcoin-roi-calculator',
      name: 'Bitcoin ROI Calculator',
      description: 'Track your Bitcoin ROI with CAGR and detailed performance metrics.',
      icon: BarChart3,
      color: 'orange',
      category: 'bitcoin'
    },
    {
      slug: 'ethereum-profit-calculator',
      name: 'Ethereum Profit Calculator',
      description: 'Calculate your ETH investment profit and potential gains.',
      icon: Gem,
      color: 'indigo',
      category: 'ethereum'
    },
    {
      slug: 'ethereum-roi-calculator',
      name: 'Ethereum ROI Calculator',
      description: 'Calculate ETH ROI including staking rewards and annualized returns.',
      icon: BarChart3,
      color: 'indigo',
      category: 'ethereum'
    },
    {
      slug: 'solana-profit-calculator',
      name: 'Solana Profit Calculator',
      description: 'Calculate your SOL investment profit with high-speed transaction support.',
      icon: Wallet,
      color: 'violet',
      category: 'altcoin'
    },
    {
      slug: 'xrp-profit-calculator',
      name: 'XRP Profit Calculator',
      description: 'Calculate profit from XRP/Ripple trades with fee calculations.',
      icon: Coins,
      color: 'slate',
      category: 'altcoin'
    },
    {
      slug: 'litecoin-profit-calculator',
      name: 'Litecoin Profit Calculator',
      description: 'Calculate your LTC investment profit - the silver to Bitcoin\'s gold.',
      icon: CircleDollarSign,
      color: 'gray',
      category: 'altcoin'
    },
    {
      slug: 'dogecoin-profit-calculator',
      name: 'Dogecoin Profit Calculator',
      description: 'Calculate your DOGE investment profit - the people\'s cryptocurrency.',
      icon: Coins,
      color: 'yellow',
      category: 'altcoin'
    },
    {
      slug: 'cardano-profit-calculator',
      name: 'Cardano Profit Calculator',
      description: 'Calculate your ADA investment profit with staking considerations.',
      icon: Gem,
      color: 'blue',
      category: 'altcoin'
    },
    {
      slug: 'polygon-profit-calculator',
      name: 'Polygon Profit Calculator',
      description: 'Calculate your MATIC investment profit - Ethereum\'s leading L2.',
      icon: Wallet,
      color: 'purple',
      category: 'altcoin'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20',
      blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20',
      purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20',
      emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20',
      cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20',
      orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400 hover:bg-orange-500/20',
      indigo: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/20',
      violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400 hover:bg-violet-500/20',
      slate: 'bg-slate-500/10 border-slate-500/30 text-slate-400 hover:bg-slate-500/20',
      gray: 'bg-gray-500/10 border-gray-500/30 text-gray-400 hover:bg-gray-500/20',
      yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20',
    };
    return colors[color] || colors.green;
  };

  const faqs = [
    {
      q: "What is a crypto calculator?",
      a: "A crypto calculator is a free online tool that helps you calculate profits, losses, ROI, and taxes from cryptocurrency trading. Updated for 2026, these calculators support Bitcoin, Ethereum, and all major altcoins, letting you input buy/sell prices and quantities to instantly see your returns."
    },
    {
      q: "How do I calculate my crypto profit?",
      a: "To calculate crypto profit: (1) Multiply your sell price by the quantity sold to get total revenue, (2) Multiply your buy price by the quantity to get total cost, (3) Subtract the cost from revenue, (4) Subtract any trading fees. The formula is: Profit = (Sell Price × Quantity) - (Buy Price × Quantity) - Fees."
    },
    {
      q: "Which crypto calculator should I use in 2026?",
      a: "Use the Profit Calculator for simple buy/sell trades, ROI Calculator for long-term investment analysis with annualized returns, Tax Calculator to estimate capital gains taxes, and Investment Calculator for DCA strategy planning. For specific coins, use our dedicated Bitcoin, Ethereum, Solana, Cardano, Dogecoin, or Polygon calculators."
    },
    {
      q: "Are these crypto calculators free?",
      a: "Yes! All our crypto calculators are 100% free to use with no signup required. They work instantly in your browser, support unlimited calculations, and don't store any of your data. Use them as often as you need for trading, tax planning, or investment analysis."
    },
    {
      q: "How accurate are crypto profit calculators?",
      a: "Our calculators provide mathematically accurate results based on the prices and quantities you enter. However, actual profits may vary due to slippage, spread, and exact fee structures on different exchanges. Always verify with your exchange's transaction history for tax reporting purposes."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://toolsforgeapp.com/" },
      { "@type": "ListItem", "position": 2, "name": "Tools", "item": "https://toolsforgeapp.com/tools" },
      { "@type": "ListItem", "position": 3, "name": "Crypto Calculators", "item": "https://toolsforgeapp.com/crypto-calculators" }
    ]
  };

  const bitcoinCalculators = calculators.filter(c => c.category === 'bitcoin');
  const ethereumCalculators = calculators.filter(c => c.category === 'ethereum');
  const generalCalculators = calculators.filter(c => c.category === 'general');
  const altcoinCalculators = calculators.filter(c => c.category === 'altcoin');

  return (
    <>
      <Helmet>
        <title>Crypto Calculators (2026) - Free Profit, ROI & Tax Tools | ToolsForge</title>
        <meta name="description" content="Free crypto calculators for 2026. Calculate Bitcoin, Ethereum & altcoin profits instantly. ROI, tax & investment tools. No signup required." />
        <meta name="keywords" content="crypto calculator 2026, bitcoin profit calculator, ethereum calculator, crypto roi calculator, crypto tax calculator, cryptocurrency calculator, btc calculator, eth calculator" />
        <link rel="canonical" href="https://toolsforgeapp.com/crypto-calculators" />
        <meta property="og:title" content="Free Crypto Calculators (2026) - Profit, ROI, Tax & More" />
        <meta property="og:description" content="Calculate cryptocurrency profits, ROI, and taxes instantly. Free tools for Bitcoin, Ethereum, and all altcoins. Updated for 2026." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://toolsforgeapp.com/crypto-calculators" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background" data-testid="crypto-calculators-hub">
        {/* Breadcrumb */}
        <div className="border-b border-border bg-card/50">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight size={14} />
              <Link to="/tools" className="hover:text-foreground transition-colors">Tools</Link>
              <ChevronRight size={14} />
              <span className="text-foreground">Crypto Calculators</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          {/* Hero Section */}
          <header className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm mb-4">
              <Calculator size={16} />
              <span>15 Free Calculators • Updated 2026</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Free Crypto Calculators (2026)
              <span className="block text-2xl sm:text-3xl lg:text-4xl text-muted-foreground mt-2">
                Profit, ROI, Tax & More
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Calculate your <strong>cryptocurrency profits</strong>, <strong>return on investment (ROI)</strong>, 
              and <strong>capital gains tax</strong> instantly. Our free crypto calculators support 
              <strong> Bitcoin</strong>, <strong>Ethereum</strong>, <strong>Solana</strong>, <strong>XRP</strong>, 
              and all major altcoins. No signup required — start calculating now.
            </p>
          </header>

          {/* Featured: Bitcoin Calculators */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <span className="text-xl font-bold text-orange-400">₿</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground">Bitcoin Calculators</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {bitcoinCalculators.map((calc) => (
                <Link
                  key={calc.slug}
                  to={`/tools/${calc.slug}`}
                  className={`p-5 rounded-xl border transition-all duration-200 group ${getColorClasses(calc.color)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <calc.icon size={24} />
                      <div>
                        <h3 className="font-semibold text-foreground group-hover:text-orange-400 transition-colors">
                          {calc.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{calc.description}</p>
                      </div>
                    </div>
                    <ArrowRight size={20} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured: Ethereum Calculators */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <span className="text-xl font-bold text-indigo-400">Ξ</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground">Ethereum Calculators</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {ethereumCalculators.map((calc) => (
                <Link
                  key={calc.slug}
                  to={`/tools/${calc.slug}`}
                  className={`p-5 rounded-xl border transition-all duration-200 group ${getColorClasses(calc.color)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <calc.icon size={24} />
                      <div>
                        <h3 className="font-semibold text-foreground group-hover:text-indigo-400 transition-colors">
                          {calc.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{calc.description}</p>
                      </div>
                    </div>
                    <ArrowRight size={20} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* General Crypto Calculators */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Coins size={20} className="text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">All Crypto Calculators</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {generalCalculators.map((calc) => (
                <Link
                  key={calc.slug}
                  to={`/tools/${calc.slug}`}
                  className={`p-5 rounded-xl border transition-all duration-200 group ${getColorClasses(calc.color)}`}
                >
                  <div className="flex items-start gap-3">
                    <calc.icon size={24} className="flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {calc.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">{calc.description}</p>
                    </div>
                    <ArrowRight size={18} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Altcoin Calculators */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Gem size={20} className="text-violet-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">Altcoin Calculators</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {altcoinCalculators.map((calc) => (
                <Link
                  key={calc.slug}
                  to={`/tools/${calc.slug}`}
                  className={`p-5 rounded-xl border transition-all duration-200 group ${getColorClasses(calc.color)}`}
                >
                  <div className="flex items-start gap-3">
                    <calc.icon size={24} className="flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {calc.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">{calc.description}</p>
                    </div>
                    <ArrowRight size={18} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* SEO Content Section */}
          <section className="mb-12 prose prose-invert max-w-none">
            <h2 className="text-2xl font-bold text-foreground mb-4">Why Use Our Crypto Calculators?</h2>
            <div className="grid md:grid-cols-2 gap-6 not-prose">
              <div className="p-5 bg-card rounded-xl border border-border">
                <h3 className="font-semibold text-foreground mb-2">100% Free & Instant</h3>
                <p className="text-muted-foreground text-sm">
                  All calculators are completely free with no signup required. Get instant results directly in your browser 
                  — no data stored, no limits on calculations.
                </p>
              </div>
              <div className="p-5 bg-card rounded-xl border border-border">
                <h3 className="font-semibold text-foreground mb-2">Accurate Calculations</h3>
                <p className="text-muted-foreground text-sm">
                  Our calculators use precise formulas for profit, ROI, CAGR, and tax calculations. Factor in trading fees, 
                  holding periods, and tax rates for accurate results.
                </p>
              </div>
              <div className="p-5 bg-card rounded-xl border border-border">
                <h3 className="font-semibold text-foreground mb-2">All Major Cryptocurrencies</h3>
                <p className="text-muted-foreground text-sm">
                  Calculate profits for Bitcoin, Ethereum, Solana, XRP, Litecoin, and any other cryptocurrency. 
                  Our general calculators work with all coins.
                </p>
              </div>
              <div className="p-5 bg-card rounded-xl border border-border">
                <h3 className="font-semibold text-foreground mb-2">Tax Planning Support</h3>
                <p className="text-muted-foreground text-sm">
                  Estimate capital gains tax for multiple countries including the US, UK, and EU. 
                  Understand your tax liability before tax season arrives.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="p-5 bg-card rounded-xl border border-border">
                  <h3 className="font-semibold text-foreground mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center p-8 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-2xl border border-green-500/20">
            <h2 className="text-2xl font-bold text-foreground mb-3">Ready to Calculate Your Crypto Profits?</h2>
            <p className="text-muted-foreground mb-6">Choose a calculator above or start with our most popular tool:</p>
            <Link
              to="/tools/crypto-profit-calculator"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
            >
              <Calculator size={20} />
              Open Crypto Profit Calculator
              <ArrowRight size={18} />
            </Link>
          </section>
        </div>
      </div>
    </>
  );
};

export default CryptoCalculatorsHub;
